import { ReactNode } from "react"

import type { PairsOf } from "utils/types"

import { css, cx } from "@emotion/css"

const styles = {
  d: css`
    display: inline-flex;
    margin: 0;
    align-items: center;
  `,
  dl: css`
    flex: 1;
    margin: 0;
    padding: 0rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, min-content));
    grid-column-gap: 58px;
    grid-row-gap: 4px;
  `,
  dc: css`
    display: flex;
    overflow: hidden;
    align-items: center;
    margin-bottom: 4px;
  `,
  dt: css`
    white-space: nowrap;
    align-self: flex-end;
  `,
  colon: css`
    &::after {
      content: ": ";
    }
  `,
  dd: css`
    margin: 0 var(--spacing-xxs);
    color: var(--black);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
  sizes: {
    default: css``,
    small: css`
      grid-template-columns: repeat(auto-fill, minmax(16ch, min-content));
    `,
  },
}

export type DescriptionListProps = {
  data: Record<string, any> | PairsOf<Record<string, any>>
  className?: string
  size?: "default" | "small"
}

export function DescriptionList(props: DescriptionListProps) {
  const { data, className, size = "default" } = props
  const pairs = Array.isArray(data) ? data : Object.entries(data)
  return (
    <dl className={cx("dl", styles.dl, styles.sizes[size], className)}>
      {pairs.map(([k, v], idx) => (
        <div key={k + idx} className={cx("dc", styles.dc)}>
          <dt
            className={cx("dt", "BodyBody-sBlackLight", styles.dt, {
              [styles.colon]: !!v,
            })}
          >
            {k}
          </dt>
          {!!v ? (
            <dd
              data-testid="description"
              className={cx("dd", "BodyBody-sBlackRegular", styles.dd)}
            >
              {v}
            </dd>
          ) : null}
        </div>
      ))}
    </dl>
  )
}

export type DescriptionItemProps = {
  label: ReactNode
  value: ReactNode
  className?: string
  keyClassName?: string
  valueClassName?: string
}
export function DescriptionItem(props: DescriptionItemProps) {
  const { label, value, className, keyClassName, valueClassName } = props
  return (
    <div className={cx("dc", styles.dc, className)}>
      <dt
        className={cx("dt", styles.dt, keyClassName, {
          [styles.colon]: !!value,
        })}
      >
        {label}
      </dt>
      {!!value ? (
        <dd className={cx("dd", styles.dd, valueClassName)}>{value}</dd>
      ) : null}
    </div>
  )
}

export type DescriptionProps = {
  label: ReactNode
  value: ReactNode
  className?: string
  keyClassName?: string
  valueClassName?: string
}
export function Description(props: DescriptionProps) {
  const { label, value, className, keyClassName, valueClassName } = props
  return (
    <dl className={cx("d", styles.d, className)}>
      <dt
        className={cx(
          "dt",
          styles.dt,
          "BodyBody-xsDark-greyRegular",
          keyClassName,
          {
            [styles.colon]: !!value,
          },
        )}
      >
        {label}
      </dt>
      {!!value ? (
        <dd
          data-testid={`description-${label
            ?.toString()
            .toLowerCase()
            .replaceAll(" ", "-")}`}
          className={cx(
            "dd",
            styles.dd,
            "BodyBody-xsBlackRegular",
            valueClassName,
          )}
        >
          {value}
        </dd>
      ) : null}
    </dl>
  )
}
